/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 */

/*
 * New Credits Component - Original Code
 * MIT Licensed - Only MIT-licensed components
 */

import React, { useEffect } from "react";
import "./Credits.css";

export default function Credits({ open, onClose }) {
  // Scroll lock when credits modal is open
  useEffect(() => {
    if (open) {
      const currentScrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.width = "100%";
      document.body.classList.add("modal-open");

      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.classList.remove("modal-open");
        window.scrollTo(0, currentScrollY);
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="new-credits-modal-bg" onClick={onClose}>
      <div className="new-credits-modal" onClick={(e) => e.stopPropagation()}>
        <button className="new-credits-modal-close" onClick={onClose}>
          ✕
        </button>
        <h2 className="new-credits-modal-title">
          <span className="gold-text">Credits & Attributions</span>
        </h2>

        <div className="new-credits-content">
          <div className="new-credit-category">
            <h3>Project Information</h3>
            <div className="new-credit-item">
              <h4>NFT Landing Page - Sailing Ships Collection</h4>
              <p>
                <strong>Current Fork:</strong> jsaif (https://github.com/jsaif)
              </p>
              <p>
                <strong>Original Project:</strong> s-shemmee (https://github.com/s-shemmee/NFT-Portfolio-Landing-Page)
              </p>
              <p>
                <strong>Design Inspiration:</strong> reacthemes (https://themeforest.net/user/reacthemes)
              </p>
              <p>
                <strong>License:</strong> GNU General Public License v3.0
              </p>
              <div className="license-text">
                <small>
                  <strong>GPL-3.0 License Notice:</strong>
                  <br />
                  This program is free software: you can redistribute it and/or modify it under the terms of the GNU
                  General Public License as published by the Free Software Foundation, either version 3 of the License,
                  or (at your option) any later version.
                  <br />
                  <br />
                  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even
                  the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the GNU General
                  Public License for more details.
                  <br />
                  <br />
                  You should have received a copy of the GNU General Public License along with this program. If not, see
                  https://www.gnu.org/licenses/.
                </small>
              </div>
            </div>
          </div>

          <div className="new-credit-category">
            <h3>Visual Effects</h3>
            <div className="new-credit-item">
              <h4>Hyperspace Tunnel Effect</h4>
              <p>
                <strong>Original by:</strong> Noah Blon
              </p>
              <p>
                <strong>Source:</strong>{" "}
                <a href="https://codepen.io/noahblon/pen/DpNRyR" target="_blank" rel="noopener noreferrer">
                  CodePen - Hyperspace
                </a>
              </p>
              <p>
                <strong>License:</strong> MIT License
              </p>
              <p>
                <strong>Attribution:</strong>
              </p>
              <div className="license-text">
                <small>
                  Copyright (c) 2024 - Noah Blon - https://codepen.io/noahblon/pen/DpNRyR
                  <br />
                  <br />
                  Permission is hereby granted, free of charge, to any person obtaining a copy of this software and
                  associated documentation files (the "Software"), to deal in the Software without restriction,
                  including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense,
                  and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so,
                  subject to the following conditions:
                  <br />
                  <br />
                  The above copyright notice and this permission notice shall be included in all copies or substantial
                  portions of the Software.
                </small>
              </div>
              <p>Modified and enhanced with React integration and improved user experience.</p>
            </div>

            <div className="new-credit-item">
              <h4>Cloud Animation Background</h4>
              <p>
                <strong>Original by:</strong> Shaw
              </p>
              <p>
                <strong>Source:</strong>{" "}
                <a href="https://codepen.io/shshaw/pen/jOMdaL" target="_blank" rel="noopener noreferrer">
                  CodePen - Floating Cloud Background
                </a>
              </p>
              <p>
                <strong>License:</strong> CodePen License (typically MIT/Open Source)
              </p>
              <p>
                <strong>Usage:</strong> About section animated cloud background
              </p>
              <p>
                <strong>Modifications:</strong> Adapted for React component, enhanced timing system with 20 cloud
                variations, staggered animation delays, and improved responsive design. Original concept of floating
                cloud background with CSS animations preserved.
              </p>
              <p>
                <strong>Attribution:</strong> Original "Floating Cloud Background" by Shaw on CodePen
              </p>
            </div>

            <div className="new-credit-item">
              <h4>Gradient Background Animation</h4>
              <p>
                <strong>Original by:</strong> Lucas Bebber
              </p>
              <p>
                <strong>Source:</strong>{" "}
                <a href="https://codepen.io/lbebber/pen/xbZKvQ" target="_blank" rel="noopener noreferrer">
                  CodePen - Gradient Background
                </a>
              </p>
              <p>
                <strong>License:</strong> CodePen License (typically MIT/Open Source)
              </p>
              <p>
                <strong>Usage:</strong> Animated gradient background effects used throughout the application
              </p>
              <p>
                <strong>Modifications:</strong> Adapted for React component integration, enhanced with CSS custom
                properties for color theming, and optimized for responsive design. Original gradient animation concept
                and CSS keyframe structure preserved.
              </p>
              <p>
                <strong>Attribution:</strong> Original gradient background animation by Lucas Bebber on CodePen
              </p>
            </div>

            <div className="new-credit-item">
              <h4>Lightspeed WebGL Shader Effect</h4>
              <p>
                <strong>Original by:</strong> Matthias Hurrle (@atzedent)
              </p>
              <p>
                <strong>Source:</strong>{" "}
                <a href="https://codepen.io/atzedent/pen/NWVYOMG" target="_blank" rel="noopener noreferrer">
                  CodePen - Lightspeed Effect
                </a>
              </p>
              <p>
                <strong>License:</strong> CodePen License (typically MIT/Open Source)
              </p>
              <p>
                <strong>Usage:</strong> WebGL2 fragment shader creating lightspeed tunnel effect
              </p>
              <p>
                <strong>Modifications:</strong> Adapted for standalone HTML with WebGL2 renderer class, enhanced error
                handling, debug information, and responsive canvas design. Original fragment shader code and
                mathematical functions for particle tunnel effect preserved.
              </p>
              <p>
                <strong>Attribution:</strong> Original "Lightspeed" WebGL shader by Matthias Hurrle (@atzedent) on
                CodePen
              </p>
            </div>
          </div>

          <div className="new-credit-category">
            <h3>Interactive Components</h3>
            <div className="new-credit-item">
              <h4>Infographic Roadmap</h4>
              <p>
                <strong>Original by:</strong> Alessio Van Keulen (AlexVanK)
              </p>
              <p>
                <strong>Source:</strong>{" "}
                <a href="https://codepen.io/AlexVanK/pen/qBaeBYV" target="_blank" rel="noopener noreferrer">
                  CodePen - Infographic Roadmap
                </a>
              </p>
              <p>
                <strong>License:</strong> CodePen License (used with attribution under CodePen's public sharing terms)
              </p>
              <p>
                <strong>Usage:</strong> Interactive step-by-step roadmap component with expandable articles
              </p>
              <p>
                <strong>Modifications:</strong> Extracted from CodePen and adapted for React component integration.
                Created both standalone HTML version and React component version with enhanced accessibility features,
                keyboard navigation, and responsive design improvements. Original design, animations, and interaction
                patterns preserved.
              </p>
              <p>
                <strong>Attribution:</strong> Original "Infographic Roadmap" by Alessio Van Keulen on CodePen
              </p>
            </div>

            <div className="new-credit-item">
              <h4>Realms Section Interactive Cards</h4>
              <p>
                <strong>Original by:</strong> z- (Zewen)
              </p>
              <p>
                <strong>Source:</strong>{" "}
                <a href="https://codepen.io/z-/pen/OBPJKK" target="_blank" rel="noopener noreferrer">
                  CodePen - Realms Cards
                </a>
              </p>
              <p>
                <strong>License:</strong> CodePen License (typically MIT/Open Source)
              </p>
              <p>
                <strong>Usage:</strong> Interactive expandable card system for realms section
              </p>
              <p>
                <strong>Modifications:</strong> Adapted for React component integration, enhanced with sailing ships
                theme, custom slideshow functionality, modal image viewers, and improved responsive design. Original
                card expansion mechanics and styling foundation preserved.
              </p>
              <p>
                <strong>Attribution:</strong> Original interactive cards concept by z- (Zewen) on CodePen
              </p>
            </div>

            <div className="new-credit-item">
              <h4>3D CSS Typography Animation</h4>
              <p>
                <strong>Original by:</strong> Guilleano Cardona
              </p>
              <p>
                <strong>Source:</strong>{" "}
                <a href="https://codepen.io/Guilleano-Cardona/pen/dyrzKqm" target="_blank" rel="noopener noreferrer">
                  CodePen - 3D CSS Typography
                </a>
              </p>
              <p>
                <strong>License:</strong> CodePen License (typically MIT/Open Source)
              </p>
              <p>
                <strong>Usage:</strong> 3D animated typography effect with layered text and perspective transforms
              </p>
              <p>
                <strong>Modifications:</strong> Adapted text content from "Pure css!" to "Sailing Ships", updated Google
                Fonts imports for new text, enhanced centering with flexbox positioning, and created both standalone
                HTML and React component versions. Original 3D layering system, animation transforms, and visual effects
                preserved.
              </p>
              <p>
                <strong>Attribution:</strong> Original "3D CSS Typography" animation by Guilleano Cardona on CodePen
              </p>
            </div>
          </div>

          <div className="new-credit-category">
            <h3>Website Design & Development</h3>
            <div className="new-credit-item">
              <h4>Original NFT Portfolio Landing Page</h4>
              <p>
                <strong>Created by:</strong> s-shemmee
              </p>
              <p>
                <strong>Repository:</strong>{" "}
                <a
                  href="https://github.com/s-shemmee/NFT-Portfolio-Landing-Page"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub - NFT-Portfolio-Landing-Page
                </a>
              </p>
              <p>
                <strong>License:</strong> GPL-3.0
              </p>
              <p>
                <strong>Design Inspiration:</strong> reacthemes Next.js landing page
              </p>
            </div>

            <div className="new-credit-item">
              <h4>Current Fork - Sailing Ships Collection</h4>
              <p>
                <strong>Maintainer:</strong> jsaif
              </p>
              <p>
                <strong>Repository:</strong>{" "}
                <a href="https://github.com/jsaif/NFT-Landing-Page" target="_blank" rel="noopener noreferrer">
                  GitHub - NFT-Landing-Page
                </a>
              </p>
              <p>
                <strong>License:</strong> GPL-3.0
              </p>
              <p>
                <strong>Major Changes:</strong> Sailing ships theme, audio system, enhanced animations, flame effects
              </p>
            </div>
          </div>

          <div className="new-credit-category">
            <h3>Third-Party Dependencies (MIT Licensed)</h3>
            <div className="new-credit-item">
              <h4>React Ecosystem</h4>
              <p>
                <strong>Components:</strong> React v18.2.0, Bootstrap v5.3.0, Three.js v0.178.0
              </p>
              <p>
                <strong>License:</strong> MIT License
              </p>
              <p>All third-party dependencies use MIT license, which is compatible with GPL-3.0</p>
            </div>
          </div>

          <div className="new-credit-category">
            <h3>Audio</h3>
            <div className="new-credit-item">
              <h4>Sound Effects</h4>
              <p>Procedurally generated using Web Audio API</p>
              <p>
                <strong>License:</strong> Original work - GPL-3.0
              </p>
            </div>

            <div className="new-credit-item">
              <h4>Background Music</h4>
              <p>Custom audio files and ambient sounds</p>
              <p>
                <strong>License:</strong> Original work - GPL-3.0
              </p>
            </div>
          </div>

          <div className="new-credit-category">
            <h3>Development</h3>
            <div className="new-credit-item">
              <h4>React Integration</h4>
              <p>Components built from scratch using React hooks and modern JavaScript</p>
              <p>
                <strong>License:</strong> GPL-3.0
              </p>
            </div>

            <div className="new-credit-item">
              <h4>UI/UX Design</h4>
              <p>Original responsive design with gold theme, transparent effects, and modern animations</p>
              <p>
                <strong>License:</strong> GPL-3.0
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
